import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

/* Loader
 * ─────────────────────────────────────────────
 * Full-screen brand intro. SUBSTITUTE TEACHER text where the maroon
 * fill animates left → right over a "ghost" outline. Pixel scan-lines
 * and a corner block of pixel mosaic give it the show's identity.
 * Auto-dismisses after ~2.0s. Fires on EVERY page refresh of the
 * storefront landing page (never on /admin/*, /checkout, etc.).
 */
export default function Loader() {
  const location = useLocation();
  const isStorefrontEntry = location.pathname === "/";

  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.location.pathname === "/";
  });

  useEffect(() => {
    if (!visible || !isStorefrontEntry) return;
    const t = setTimeout(() => {
      // Used by Hero's PixelBurst to know whether to delay its sprinkle
      // until after the intro has cleared.
      sessionStorage.setItem("st-loader-shown", "1");
      setVisible(false);
    }, 2100);
    return () => clearTimeout(t);
  }, [visible, isStorefrontEntry]);

  if (!isStorefrontEntry) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="st-loader"
          data-testid="brand-loader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, y: "-100%" }}
          transition={{ duration: 0.7, ease: [0.7, 0, 0.2, 1] }}
          className="fixed inset-0 z-[100] bg-[var(--st-bg)] flex items-center justify-center overflow-hidden"
        >
          {/* TV scan lines */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none opacity-25 mix-blend-multiply"
            style={{
              backgroundImage:
                "repeating-linear-gradient(0deg, rgba(26,20,16,0.12) 0 1px, transparent 1px 4px)",
            }}
          />

          {/* corner pixel mosaic */}
          <div className="absolute top-6 left-6 flex flex-col gap-[3px]">
            {["■■..■", ".■■.■", "■■■.."].map((row, r) => (
              <div key={r} className="flex gap-[3px]">
                {row.split("").map((c, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.05 * (r * 5 + i), duration: 0.18 }}
                    className="block w-3 h-3 md:w-4 md:h-4"
                    style={{ background: c === "■" ? "var(--st-mustard)" : "transparent" }}
                  />
                ))}
              </div>
            ))}
          </div>

          {/* live chip */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="absolute top-6 right-6 inline-flex items-center gap-2 border-2 border-[#111] bg-[var(--st-cream)] px-3 py-1.5 shadow-brutal-sm"
          >
            <span className="w-2 h-2 bg-[var(--st-maroon)] animate-pulse rounded-full" />
            <span className="font-heading uppercase tracking-[0.22em] text-[10px] md:text-xs">
              Roll Call · Loading
            </span>
          </motion.div>

          {/* MAIN STAGE — two stacked title lines that "fill" with maroon */}
          <div className="relative px-4 sm:px-6 select-none w-full max-w-[1400px]">
            <FillReveal
              text="SUBSTITUTE"
              ghostColor="rgba(123, 30, 42, 0.18)"
              fillColor="var(--st-maroon)"
              accentColor="var(--st-mustard)"
              accentLetters={[0, 1, 2]}
              delay={0.1}
            />
            <FillReveal
              text="TEACHER"
              ghostColor="rgba(212, 164, 55, 0.22)"
              fillColor="var(--st-maroon)"
              accentColor="var(--st-gold)"
              accentLetters={[1, 2]}
              delay={0.6}
              className="-mt-2 md:-mt-4 text-center"
            />

            {/* progress bar */}
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ duration: 1.8, ease: "easeInOut" }}
              className="absolute -bottom-6 left-0 h-[3px] bg-[var(--st-maroon)]"
            />
          </div>

          {/* bottom credits */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 font-heading uppercase tracking-[0.3em] text-[10px] md:text-xs text-[var(--st-fg)]/60"
          >
            <span>Wall of Entertainment</span>
            <span className="w-1 h-1 bg-[var(--st-maroon)]" />
            <span>Tubi TV</span>
            <span className="w-1 h-1 bg-[var(--st-maroon)]" />
            <span>Drop 01</span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ─── Internal: animated word with maroon fill sweep over ghost outline ─── */
function FillReveal({
  text,
  ghostColor,
  fillColor,
  accentColor,
  accentLetters = [],
  delay = 0,
  className = "",
}) {
  return (
    <div className={`relative text-center ${className}`} aria-label={text}>
      {/* ghost outline layer (always visible). Font size uses clamp() so even
          the long word "SUBSTITUTE" (10 chars) fits inside the viewport on
          phones and never spills out on huge desktops. */}
      <h1
        aria-hidden
        className="font-display uppercase leading-[0.85] tracking-tight whitespace-nowrap"
        style={{ color: ghostColor, fontSize: "clamp(2.5rem, 13vw, 200px)" }}
      >
        {text.split("").map((ch, i) => (
          <span key={i}>{ch}</span>
        ))}
      </h1>

      {/* fill layer — clipped left→right sweep */}
      <motion.h1
        initial={{ clipPath: "inset(0 100% 0 0)" }}
        animate={{ clipPath: "inset(0 0% 0 0)" }}
        transition={{ delay, duration: 1.1, ease: [0.7, 0, 0.3, 1] }}
        className="absolute inset-0 font-display uppercase leading-[0.85] tracking-tight whitespace-nowrap"
        style={{ color: fillColor, fontSize: "clamp(2.5rem, 13vw, 200px)" }}
      >
        {text.split("").map((ch, i) => (
          <span
            key={i}
            style={accentLetters.includes(i) ? { color: accentColor } : undefined}
          >
            {ch}
          </span>
        ))}
      </motion.h1>
    </div>
  );
}
